import { Editor, Element, NodeEntry, Path, Range, Text, Transforms } from "slate";

import { trackEngagement } from "helpers/analytics";
import { isUrl, normalizeBrowserLink } from "utils/string";

import { LinkElement, RichEditor } from "../types";

const isLinkActive = (editor: RichEditor): boolean => {
  const [link] = Editor.nodes(editor, { match: n => Element.isElement(n) && n.type === "link" });
  return !!link;
};

const unwrapLink = (editor: RichEditor): void => {
  Transforms.unwrapNodes(editor, { match: n => Element.isElement(n) && n.type === "link" });
};

const wrapLink = (editor: RichEditor, url: string): void => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { success, url: normalizedURL } = normalizeBrowserLink(url.trim());

  if (!success) {
    Transforms.insertText(editor, url);
    return;
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link = {
    type: "link",
    url: normalizedURL,
    children: isCollapsed ? [{ text: url }] : [],
  } as LinkElement;

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Editor.withoutNormalizing(editor, () => {
      Transforms.wrapNodes(editor, link, {
        split: true,
        match: node =>
          "text" in node
            ? node.text !== ""
            : node.children.length > 1 ||
              (node.children.length === 1 && "text" in node.children[0] && node.children[0].text !== ""),
      });
      Transforms.collapse(editor, { edge: "end" });
    });
  }
};

const USER_DEFINED_URL_REGEX = /^www\.[a-zA-Z0-9_-]+?(?:\.?[a-zA-Z0-9_-]+?)+(?:\/\S+)*$/;

const wrapInTextLink = (editor: Editor): void => {
  if (!editor.selection || Range.isExpanded(editor.selection)) {
    return;
  }

  const lineStart = Editor.start(editor, editor.selection.anchor.path);
  const lineEnd = editor.selection.anchor;

  if (!lineStart) {
    return;
  }

  const lineText = Editor.string(editor, Editor.range(editor, lineStart, lineEnd));

  const linePieces = lineText.split(/\s+?/);
  const lastWord = linePieces[linePieces.length - 1];

  if (!lastWord) {
    return;
  }

  if (USER_DEFINED_URL_REGEX.test(lastWord) || isUrl(lastWord)) {
    const urlStart = Editor.before(editor, editor.selection, { unit: "offset", distance: lastWord.length });
    // @ts-expect-error TS(2345)
    const urlRange = Editor.range(editor, urlStart, lineEnd);
    const { success, url } = normalizeBrowserLink(lastWord);

    if (!success) {
      return;
    }

    const [linkElement] = Editor.nodes(editor, {
      at: urlRange,
      match: node => Element.isElement(node) && node.type === "link",
      mode: "lowest",
    });

    if (!linkElement) {
      Transforms.wrapNodes(
        editor,
        { type: "link", url: url.trim(), children: [] },
        { at: urlRange, mode: "lowest", match: () => true, split: true }
      );
      trackEngagement("Editor: Link added", editor.eventPlace, { source: "copy-paste" });
    }
  }
};

export function withLinks(editor: Editor): Editor {
  const { insertText, insertBreak, insertData, isInline, normalizeNode } = editor;

  editor.isInline = (element): boolean => (element.type === "link" ? true : isInline(element));

  editor.insertText = (text): void => {
    if (text && isUrl(text)) {
      trackEngagement("Editor: Link added", editor.eventPlace, { source: "copy-paste" });
      wrapLink(editor, text);
    } else {
      if (/\s|\n/.test(text)) {
        Transforms.insertFragment(editor, [{ type: "paragraph", children: [{ text: "" }] }]);
      }

      insertText(text);
    }
  };

  editor.insertBreak = (): void => {
    wrapInTextLink(editor);
    insertBreak();
  };

  editor.insertData = (data: DataTransfer): void => {
    const text = data.getData("text/plain");

    if (text && isUrl(text)) {
      trackEngagement("Editor: Link added", editor.eventPlace, { source: "copy-paste" });
      wrapLink(editor, text);
    } else {
      insertData(data);
    }
  };

  editor.normalizeNode = (entry: NodeEntry): void => {
    const [node, path] = entry;
    const { selection } = editor;

    if (
      Element.isElement(node) &&
      node.type === "link" &&
      node.children.length === 1 && //we need to filter nodes with links that was deleted from editor but they are still in RTO but invisible
      node.children.filter(child => Text.isText(child)).every(child => (child as Text).text === "")
    ) {
      Transforms.removeNodes(editor, { at: path });
    }

    if (
      Element.isElement(node) &&
      node.type === "link" &&
      Editor.string(editor, path) === "" &&
      !node.children.some(child => Element.isElement(child) && editor.isVoid(child))
    ) {
      const start = Editor.start(editor, path);
      const end = Editor.end(editor, path);

      if (!selection) {
        Transforms.unwrapNodes(editor, { at: path, match: Element.isElement, split: true });
        return;
      }

      if (selection && start && end && !Range.includes(Editor.range(editor, start, end), selection)) {
        Transforms.unwrapNodes(editor, { at: path, match: Element.isElement, split: true });
        return;
      }

      if (!Path.equals(path, [])) {
        const [parentNode, parentPath] = Editor.parent(editor, path);
        if (
          Element.isElement(parentNode) &&
          parentNode.type === "paragraph" &&
          Editor.string(editor, path) === "" &&
          !node.children.some(child => Element.isElement(child) && editor.isVoid(child)) &&
          parentNode.children.indexOf(node) === 0
        ) {
          Editor.withoutNormalizing(editor, () => {
            Transforms.insertNodes(editor, [{ text: "" }], {
              at: { path: parentPath, offset: 0 },
              match: Element.isElement,
              select: true,
            });
            Transforms.removeNodes(editor, { match: Element.isElement, at: Path.next(path) });
            Transforms.select(editor, Editor.start(editor, Path.next(path)));
          });
        }
      }
    }

    normalizeNode(entry);
  };

  return editor;
}

const ReactPart: import("./index").ReactPart = {
  renderElement(props, _editor, next) {
    const { element, attributes, children } = props;
    if (element.type === "link") {
      return (
        <a
          href={(element as LinkElement).url}
          {...attributes}
          data-testid={`editor-link-${(element as LinkElement).url}`}
        >
          {children}
        </a>
      );
    }
    return next(props);
  },
};

const pluginSpec: import("./index").PluginSpec = [withLinks, ReactPart];
export default pluginSpec;
